import { useLayoutEffect, useRef, useState, type PointerEvent } from 'react';
import { scaleLinear } from 'd3-scale';

import { cn } from '../lib/cn';

export interface IAreaPoint {
  /** What the tooltip names the point by: "24 Aug". */
  label: string;
  value: number;
  /** Approximate: its segments are drawn dashed over a fainter fill. */
  estimated?: boolean;
}

interface IAreaChartProps {
  points: readonly IAreaPoint[];
  /** Gridlines, bottom first; the first is the baseline. */
  ticks: readonly number[];
  tickText: (value: number) => string;
  valueText: (value: number) => string;
  /** Indices of `points` labelled under the axis; the last one reads "today". */
  axisAt: readonly number[];
  axisText: (index: number) => string;
  label: string;
  className?: string;
}

const LEFT = 40;
const RIGHT = 6;
const TOP = 8;
const BOTTOM = 20;
const ESTIMATED_DASH = '4 3';

export const AreaChart = ({
  points,
  ticks,
  tickText,
  valueText,
  axisAt,
  axisText,
  label,
  className,
}: IAreaChartProps) => {
  const box = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });
  const [hover, setHover] = useState<number | null>(null);

  useLayoutEffect(() => {
    const node = box.current;
    if (!node) return;
    const observer = new ResizeObserver(([entry]) => {
      if (entry) setSize({ width: entry.contentRect.width, height: entry.contentRect.height });
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const { width, height } = size;
  const top = ticks[ticks.length - 1] ?? 1;
  const x = scaleLinear()
    .domain([0, Math.max(1, points.length - 1)])
    .range([LEFT, Math.max(LEFT + 1, width - RIGHT)]);
  const y = scaleLinear()
    .domain([0, top || 1])
    .range([Math.max(TOP + 1, height - BOTTOM), TOP]);
  const last = points.length - 1;
  // One piece per segment, so the estimated stretch can be dashed without a second chart.
  const segments = { exact: { line: '', area: '' }, estimated: { line: '', area: '' } };
  for (let i = 1; i <= last; i += 1) {
    const [from, to] = [points[i - 1]!, points[i]!];
    const into = from.estimated || to.estimated ? segments.estimated : segments.exact;
    into.line += `M${x(i - 1)},${y(from.value)}L${x(i)},${y(to.value)}`;
    into.area += `M${x(i - 1)},${y(from.value)}L${x(i)},${y(to.value)}L${x(i)},${y(0)}L${x(i - 1)},${y(0)}Z`;
  }

  const onMove = (event: PointerEvent<SVGSVGElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const index = Math.round(x.invert(event.clientX - rect.left));
    setHover(Math.min(last, Math.max(0, index)));
  };
  const hovered = hover === null ? null : points[hover];

  return (
    <div ref={box} className={cn('relative h-chart-storage w-full', className)}>
      {width > 0 && (
        <svg
          width={width}
          height={height}
          role="img"
          aria-label={label}
          className="block"
          onPointerMove={onMove}
          onPointerLeave={() => setHover(null)}
        >
          {ticks.map((tick) => (
            <g key={tick}>
              <line
                x1={LEFT}
                x2={width - RIGHT}
                y1={y(tick)}
                y2={y(tick)}
                className="stroke-border"
              />
              <text
                x={LEFT - 6}
                y={y(tick) + 3}
                textAnchor="end"
                className="fill-faint font-mono text-caps"
              >
                {tickText(tick)}
              </text>
            </g>
          ))}
          <path d={segments.exact.area} className="fill-highlight opacity-60" />
          <path d={segments.estimated.area} className="fill-highlight opacity-30" />
          <path d={segments.exact.line} fill="none" strokeWidth={2} className="stroke-accent" />
          <path
            d={segments.estimated.line}
            fill="none"
            strokeWidth={2}
            strokeDasharray={ESTIMATED_DASH}
            className="stroke-accent opacity-60"
          />
          {last >= 0 && (
            <circle cx={x(last)} cy={y(points[last]!.value)} r={3} className="fill-accent" />
          )}
          {axisAt.map((index) => (
            <text
              key={index}
              x={x(index)}
              y={height - 4}
              textAnchor={index === 0 ? 'start' : index === last ? 'end' : 'middle'}
              className="fill-faint font-mono text-caps"
            >
              {axisText(index)}
            </text>
          ))}
          {hover !== null && hovered && (
            <g>
              <line
                x1={x(hover)}
                x2={x(hover)}
                y1={TOP}
                y2={y(0)}
                className="stroke-border-strong"
              />
              <circle cx={x(hover)} cy={y(hovered.value)} r={4} className="fill-accent" />
            </g>
          )}
        </svg>
      )}
      {hovered && hover !== null && (
        <div
          className={cn(
            'pointer-events-none absolute top-0 rounded-control border border-border-raised',
            'bg-tooltip px-inset py-label font-mono text-micro text-primary shadow-panel',
            hover > last / 2 ? '-translate-x-full' : 'translate-x-2',
          )}
          style={{ left: x(hover) }}
        >
          {hovered.label} · {hovered.estimated ? '≈ ' : ''}
          {valueText(hovered.value)}
        </div>
      )}
    </div>
  );
};
